import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { attachCache } from '../utils/cache/axiosCache';
import { sessionHeaders, isAccountClosedDetail, forceLogout } from '../utils/session';

const CHAT_API_URL =
  process.env.REACT_APP_CHAT_API_URL ||
  'http://localhost:8001/chat-api';

const api: AxiosInstance = axios.create({
  baseURL: CHAT_API_URL,
  timeout: 30000,
});

// Separate instance for refresh calls — no 401 interceptor so it won't loop.
const refreshApi = axios.create({ baseURL: CHAT_API_URL, timeout: 15000 });

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

function getClientDeviceId(): string {
  const key = 'teamlio.call.deviceId.v1';
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const generated = globalThis.crypto?.randomUUID?.()
      ?? `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(key, generated);
    return generated;
  } catch {
    return 'web-session';
  }
}

function processQueue(newToken: string) {
  refreshQueue.forEach((cb) => cb(newToken));
  refreshQueue = [];
}

async function attemptRefresh(): Promise<string | null> {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    const accessToken = localStorage.getItem('accessToken');
    const res = await refreshApi.post(
      '/api/auth/refresh',
      refreshToken ? { refresh_token: refreshToken } : {},
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const newToken: string = res.data?.access_token ?? res.data?.accessToken;
    if (!newToken) return null;
    localStorage.setItem('accessToken', newToken);
    if (res.data?.refresh_token) {
      localStorage.setItem('refreshToken', res.data.refresh_token);
    }
    return newToken;
  } catch {
    return null;
  }
}

function redirectToLogin() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  try {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['X-Device-ID'] = getClientDeviceId();
      // Single-session identity. Separate from X-Device-ID above, which is the
      // calling/WebRTC device key and predates this feature — do not merge
      // them: changing X-Device-ID's storage key would reshuffle live calls.
      Object.assign(config.headers, await sessionHeaders());
    }
  } catch {
    /* ignore storage errors */
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // The account itself is closed — deactivated, or its sign-in locked by an
    // admin. Chat learns this from TeamLeo (/api/internal/account-state) and
    // relays the sentence. No refresh can save it, and the refresh path would
    // land the user on a bare login screen with no idea why, so sign out with
    // the reason instead.
    if (error.response?.status === 401
        && isAccountClosedDetail((error.response?.data as any)?.detail)) {
      forceLogout((error.response.data as any).detail);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;

      if (isRefreshing) {
        // Queue requests while a refresh is in progress.
        return new Promise((resolve, reject) => {
          refreshQueue.push((token) => {
            if (original.headers) original.headers.Authorization = `Bearer ${token}`;
            resolve(api(original));
          });
          // If refresh ultimately fails the queue will be cleared by the outer catch.
          setTimeout(() => reject(error), 15000);
        });
      }

      isRefreshing = true;
      const newToken = await attemptRefresh();
      isRefreshing = false;

      if (newToken) {
        processQueue(newToken);
        if (original.headers) original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }

      // Refresh failed — clear queue and send user to login.
      refreshQueue = [];
      redirectToLogin();
    }

    return Promise.reject(error);
  }
);

// Read-through disk cache. Registered last so the 401-refresh interceptor above
// gets first refusal — this layer must only see failures that cannot be retried.
attachCache(api, 'chat');

export default api;
export { CHAT_API_URL };
